import { useState } from 'react';
import { useAuth } from 'hooks/useAuth';
import { UserAvatar } from '@/components/UserAvatar';
import { TwoFactor } from './TwoFactor';
import { ChangePassword } from './ChangePassword';
import { BadgeColour } from './BadgeColour';
import { ProfilePhoto } from './ProfilePhoto';
import { EmailNotifications } from '../Notifications';
import { PillTabs, type PillOption } from '../../ConsolePanel';

type AccountTab = 'tfa' | 'password' | 'notifications';

const TABS: readonly PillOption[] = [
  { value: 'tfa', label: 'Passkeys' },
  { value: 'password', label: 'Password' },
  { value: 'notifications', label: 'Notifications' },
];

/**
 * Your own account.
 *
 * The page used to open on a row of three pills with nothing above them, which is why
 * it read as unfinished: a settings page needs a subject, and this one is about a
 * person. The identity strip says whose account is being changed - which matters
 * because the header's account picker switches organizations, not users, and the two
 * are easy to conflate.
 *
 * The pills come from the shared `PillTabs` rather than a local copy. This screen had
 * its own, which is how the two drifted into different heights and focus treatments.
 */
export function AccountSettings() {
  const [tab, setTab] = useState<AccountTab>('tfa');
  const { user } = useAuth();

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-[14px]">
      <section
        className="bg-panel border-line rounded-panel flex flex-wrap items-center gap-3 border p-3.5"
        data-tour="settings-account-identity"
      >
        {!!user && <UserAvatar user={user} size="lg" />}

        <div className="min-w-0">
          <p
            className="text-foreground m-0 truncate text-title font-medium"
            title={`${user?.firstName} ${user?.lastName}`}
          >
            {user?.firstName} {user?.lastName}
          </p>
          <p className="supporting m-0 truncate" title={user?.email}>
            {user?.email}
          </p>
        </div>

        {!!user?.role && (
          <span className="border-line bg-field text-dim rounded-pill ml-auto shrink-0 border px-2 py-0.5 font-mono text-nano tracking-[0.1em] uppercase">
            {user.role}
          </span>
        )}
      </section>

      {/* Photo first: it is what is shown when there is one, and the colour panel says so. */}
      <ProfilePhoto />
      <BadgeColour />

      {/* A plain block around the strip, for the product tour: PillTabs takes no anchor of its own. */}
      <div data-tour="settings-account-tabs">
        <PillTabs options={TABS} value={tab} onChange={(next) => setTab(next as AccountTab)} />
      </div>

      {tab === 'tfa' && <TwoFactor />}
      {tab === 'password' && <ChangePassword />}
      {tab === 'notifications' && <EmailNotifications />}
    </div>
  );
}
